import {
  format,
  fromUnixTime,
  parse,
  setMinutes,
  sub,
  add,
} from "date-fns-jalali";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ReactSVG } from "react-svg";
import { useCookies } from "react-cookie";

import { getBaseDataLawyer } from "../../../redux/action/baseData/baseAction";
import { AddEventToList } from "../../../redux/action/events/eventsAction";

import Header from "../../../components/Header";
import Button from "../../../components/UI/Button";
import AddEventForm from "../../../components/Events/Add/Form";
import ToastCustom from "../../../components/UI/ToastCustom";
import LoadingDot from "../../../components/UI/LoadingDot";
import moment from "jalali-moment";
import { ADD_EVENT_TO_HOME_INFO } from "../../../redux/action/home/homeType";
import { ADD_EVENT_TO_SINGLE_CASE } from "../../../redux/action/cases/casesType";
import { toast } from "react-toastify";
import { SET_FIELDS_EVENTS } from "../../../redux/action/events/eventsType";
import {
  eventWallMoment,
  resolveAlarms,
  toServerString,
} from "../../../utils/alarms";

const AddEvent = () => {
  const dispatch = useDispatch();
  const canISendData = useSelector((state) => state.events.canISendData);
  const fields = useSelector((state) => state.events.fields);
  const error = useSelector((state) => state.events.error);
  const loading = useSelector((state) => state.events.loading);
  const homeInfoLoaded = useSelector((state) => state.home.loaded);
  const singlesCase = useSelector((state) => state.cases.singlesCase);
  const differentServerDate = useSelector(
    (state) => state.events.computerDifferentTime,
  );

  const [persianDate, setPersianDate] = useState("");
  const [englishDate, setEnglishDate] = useState("");

  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  const [cookies] = useCookies(["authToken"]);

  useEffect(() => {
    setEnglishDate(params.date.split("-").join("/"));

    if (location?.state?.persianEnglishDate) {
      setPersianDate(location?.state?.persianEnglishDate);
      return;
    } else getPersianDate();

    window.scrollTo({ top: 0 });
  }, []);

  const getPersianDate = () => {
    let month = params.date.slice(5, 7);
    month = Number(month) - 1;
    const time = new Date(params.date.slice(0, 4), month, params.date.slice(8));
    const date = format(time, "yyyy/MM/dd", { jalali: true });

    setPersianDate(date);
  };

  // push a created event into Home / single-case stores (same as before, per event)
  const syncCreatedEvent = (response, payload) => {
    if (homeInfoLoaded) {
      // add to Home only when it is today / tomorrow
      const now = add(new Date(new Date().valueOf() + differentServerDate), {
        days: 2,
      }).valueOf();
      const eventTime = Date.parse(moment(response.starts_at));

      if (eventTime <= now) {
        dispatch({
          type: ADD_EVENT_TO_HOME_INFO,
          payload: response,
        });
      }
    }

    if (
      payload?.law_suit_id &&
      singlesCase &&
      singlesCase[payload.law_suit_id]
    ) {
      dispatch({
        type: ADD_EVENT_TO_SINGLE_CASE,
        payload: {
          caseId: payload.law_suit_id,
          data: response,
        },
      });
    }
  };

  const addEvent = async () => {
    const eventWall = eventWallMoment(params.date, fields.clock);

    if (!eventWall) {
      toast.error("زمان رویداد معتبر نیست");
      return;
    }

    const startsAt = toServerString(eventWall);

    // base payload – identical to the old single-event payload
    const baseData = {
      title: fields.title.value,
      type: fields.type ? fields.type : "",
      description: fields.description.value,
      law_suit_id: fields.caseInfo.id ? fields.caseInfo.id : null,
      is_public: fields.showEventToMovakel,
      send_sms: fields.sendSms,

      alaram_at: null,
      alarm_at: null,
      starts_at: startsAt,
      finishes_at: null,
      is_started: false,

      // my structure
      event_date: startsAt,
      event_alaram_at: null,
    };

    // one event per alarm (the API accepts a single alarm_at per event).
    // Alarms already in the past are dropped here as a final safety net.
    const nowMs = Date.now() + (differentServerDate || 0);
    const selectedAlarms = fields.alarms || [];
    const resolved = resolveAlarms(selectedAlarms, eventWall, nowMs);
    const skipped = selectedAlarms.length - resolved.length;

    // closest alarm to the event first: it is the "main" event and keeps the
    // original title; the rest are reminders, farthest ones last
    const ordered = [...resolved].reverse();

    const payloads = ordered.length
      ? ordered.map(({ serverString }, index) => ({
          ...baseData,
          title:
            index === 0
              ? baseData.title
              : `یادآور رویداد: ${baseData.title}`,
          // the client app should see ONE event, not N copies
          is_public: index === 0 ? baseData.is_public : false,
          alaram_at: serverString,
          alarm_at: serverString,
          event_alaram_at: serverString,
        }))
      : [baseData];

    if (skipped > 0) {
      toast.info(
        `${skipped.toLocaleString("fa")} هشدار که زمانش گذشته بود ثبت نشد`,
      );
    }

    // send one by one so we know exactly how many were created
    let createdCount = 0;

    for (const payload of payloads) {
      const response = await dispatch(
        await AddEventToList(payload, cookies.authToken),
      );

      if (!response) break;

      createdCount += 1;
      syncCreatedEvent(response, payload);
    }

    if (createdCount < payloads.length) {
      if (createdCount > 0) {
        // keep only the alarms that were NOT created, so pressing "create"
        // again can't duplicate the ones that already went through
        dispatch({
          type: SET_FIELDS_EVENTS,
          payload: {
            ...fields,
            alarms: ordered.slice(createdCount).map(({ alarm }) => alarm),
          },
        });

        toast.error(
          `فقط ${createdCount.toLocaleString("fa")} از ${payloads.length.toLocaleString(
            "fa",
          )} هشدار ثبت شد. دوباره «ایجاد» را بزنید تا بقیه ثبت شوند.`,
        );
      }

      return;
    }

    if (!location?.state?.forAddCase)
      navigate(
        location.key !== "default"
          ? -1
          : `/events/date/${baseData.event_date.slice(0, 10)}`,
        { replace: true },
      );
    else navigate(`/law-suits/${baseData?.law_suit_id}`, { replace: true });
  };

  return (
    <>
      <Header title={"تنظیم رویداد"} backBtn={true}>
        <Button
          className={`profile-btn bg-primary py-1 px-4 ${!canISendData && !loading ? "opacity-60" : ""}`}
          onClick={() => {
            if (!canISendData && !loading) {
              toast.error("وارد کردن فیلد عنوان الزامی است");
            } else {
              addEvent();
            }
          }}
        >
          ایجاد
        </Button>
      </Header>

      <main className="main-content">
        <AddEventForm persianDate={persianDate} englishDate={englishDate} />

        {error && (
          <ToastCustom>
            <div className="flex items-center gap-2">
              <ReactSVG
                className="text-rose-500/90 4xm:w-8 w-6"
                src={`${process.env.PUBLIC_URL}/assets/images/icons/info-box.svg`}
              />

              {error}
            </div>
          </ToastCustom>
        )}

        {loading && <LoadingDot />}
      </main>
    </>
  );
};

export default AddEvent;
